import { Navigate, Route, Routes } from 'react-router-dom'
import { useEffect } from 'react'

import { AuthProvider, useAuth } from './auth/AuthContext.js'
import { AuthGate } from './auth/AuthGate.js'
import { SkipLink } from './components/SkipLink'
import { Toast } from './components/Toast'
import {
  DEFAULT_PREFERENCES,
  applyPreferencesToDocument,
  listenToPreferences,
} from './lib/preferences.js'
import { enableOfflinePersistence } from './lib/firebase.js'
import CallbackPage from './pages/CallbackPage'
import HomePage from './pages/HomePage'
import PendingPage from './pages/PendingPage'
import AdminLayout from './pages/admin/AdminLayout'
import AuditPage from './pages/admin/AuditPage'
import ModerationPage from './pages/admin/ModerationPage'
import StatsPage from './pages/admin/StatsPage'
import UsersPage from './pages/admin/UsersPage'
import ClassAnalyticsPage from './pages/teacher/ClassAnalyticsPage'
import ClassDetailPage from './pages/teacher/ClassDetailPage'
import ClassesPage from './pages/teacher/ClassesPage'
import ClassroomConnectCallbackPage from './pages/teacher/ClassroomConnectCallbackPage'
import DeckEditorPage from './pages/teacher/DeckEditorPage'
import DecksPage from './pages/teacher/DecksPage'
import ImportQuizletPage from './pages/teacher/ImportQuizletPage'
import TeacherLayout from './pages/teacher/TeacherLayout'
import FlashcardsPage from './pages/student/FlashcardsPage'
import MyDecksPage from './pages/student/MyDecksPage'
import SpeedMatchPage from './pages/student/SpeedMatchPage'
import StudentDeckEditorPage from './pages/student/StudentDeckEditorPage'
import StudentHomePage from './pages/student/StudentHomePage'
import StudentLayout from './pages/student/StudentLayout'

export default function App() {
  return (
    <AuthProvider>
      <OfflinePersistenceBootstrap />
      <PreferencesBootstrap />
      <SkipLink />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/auth/callback" element={<CallbackPage />} />
        <Route
          path="/pending"
          element={
            <AuthGate allow={['pending']}>
              <PendingPage />
            </AuthGate>
          }
        />
        <Route
          path="/student"
          element={
            <AuthGate allow={['student']}>
              <StudentLayout />
            </AuthGate>
          }
        >
          <Route index element={<StudentHomePage />} />
          <Route path="decks/:deckId/flashcards" element={<FlashcardsPage />} />
          <Route path="decks/:deckId/speedmatch" element={<SpeedMatchPage />} />
          <Route path="my-decks" element={<MyDecksPage />} />
          <Route path="my-decks/:deckId" element={<StudentDeckEditorPage />} />
        </Route>
        <Route
          path="/teacher"
          element={
            <AuthGate allow={['teacher', 'admin']}>
              <TeacherLayout />
            </AuthGate>
          }
        >
          <Route index element={<Navigate to="/teacher/decks" replace />} />
          <Route path="decks" element={<DecksPage />} />
          <Route path="decks/:deckId" element={<DeckEditorPage />} />
          <Route path="classes" element={<ClassesPage />} />
          <Route path="classes/:classId" element={<ClassDetailPage />} />
          <Route path="classes/:classId/analytics" element={<ClassAnalyticsPage />} />
          <Route path="classroom/connect/callback" element={<ClassroomConnectCallbackPage />} />
          <Route path="import" element={<ImportQuizletPage />} />
        </Route>
        <Route
          path="/admin"
          element={
            <AuthGate allow={['admin']}>
              <AdminLayout />
            </AuthGate>
          }
        >
          <Route index element={<Navigate to="/admin/users" replace />} />
          <Route path="users" element={<UsersPage />} />
          <Route path="audit" element={<AuditPage />} />
          <Route path="moderation" element={<ModerationPage />} />
          <Route path="stats" element={<StatsPage />} />
        </Route>
        <Route path="*" element={<PlaceholderPage title="Page not found" />} />
      </Routes>
      <Toast />
    </AuthProvider>
  )
}

function PreferencesBootstrap(): null {
  const { uid } = useAuth()

  useEffect(() => {
    if (!uid) {
      applyPreferencesToDocument(DEFAULT_PREFERENCES)
      return
    }
    return listenToPreferences(uid, applyPreferencesToDocument)
  }, [uid])

  return null
}

function OfflinePersistenceBootstrap(): null {
  useEffect(() => {
    void enableOfflinePersistence()
  }, [])

  return null
}

function PlaceholderPage({ title }: { title: string }) {
  return (
    <main id="main-content">
      <h1>{title}</h1>
      <p>Coming soon.</p>
    </main>
  )
}
